import { twMerge } from "tailwind-merge";
import { useMatch } from "react-router-dom";
import { Search } from "lucide-react";
import { NavLink, Link } from "react-router-dom";
import { Button } from "@shared/ui/Button";
import { PollinatorLogo } from "@shared/ui/PollinatorLogo";
import { PlatformAvatarDropdown } from "@shared/ui/PlatformAvatarDropdown";
import { User } from "@platform/types/platform.types";
import NotificationsNavButtonContainer from "@platform/features/notifications/components/NotificationsNavButton/NotificationsNavButtonContainer";
import { ChatButtonContainer } from "@platform/features/chat/components/ChatButton/ChatButtonContainer";
import { useChatVisibility } from "@platform/features/chat/hooks/useChatVisibility";
import { dashboardNavigationItemsData } from "./dashboardNavigationItemsData";

export type Props = {
  onOpenSearch: () => void;
  onNavigationClick: (
    e: React.MouseEvent<HTMLAnchorElement>,
    to: string
  ) => void;
  user: User | null;
  isChatDisabled: boolean;
};

export function DashboardNavigationItems({
  onOpenSearch,
  onNavigationClick,
  user,
  isChatDisabled,
}: Props) {
  const fullName = `${user?.first_name} ${user?.last_name}`;
  const { shouldShowChat } = useChatVisibility(isChatDisabled);

  return (
    <>
      <nav className="flex flex-col w-full justify-start items-start gap-6 px-4 py-4 md:flex-row md:items-center md:justify-center md:gap-0 md:py-0 md:px-0">
        <a
          href="/dashboard"
          title="Dashboard"
          aria-label="Dashboard"
          className="mb-8 py-2 px-2 md:hidden"
          data-testid="dashboard-nav-home"
        >
          <PollinatorLogo size="sm" />
        </a>
        {dashboardNavigationItemsData.map((item) => {
          const match = useMatch(item.href);
          const isActive = Boolean(match);

          return (
            <Button
              as={NavLink}
              to={item.href}
              key={item.title}
              onClick={(e: React.MouseEvent<HTMLAnchorElement>) =>
                onNavigationClick(e, item.href)
              }
              variant="ghost"
              className={twMerge(
                "w-full justify-start md:w-auto md:justify-center",
                isActive ? "bg-brand-200" : ""
              )}
              data-testid={`dashboard-nav-${item.title.toLowerCase()}`}
            >
              {item.title}
            </Button>
          );
        })}
      </nav>

      <div>
        <Button
          as={Link}
          to="/logout"
          onClick={(e: React.MouseEvent<HTMLAnchorElement>) =>
            onNavigationClick(e, "/logout")
          }
          variant="ghost"
          className="py-8 px-4 justify-start md:hidden"
          data-testid="dashboard-nav-logout"
          title="Log out"
          aria-label="Log out"
        >
          Log out
        </Button>
        <div className="py-6 border-t border-brand-800 md:border-none md:py-0">
          <div className="relative flex flex-col items-start gap-3 px-4 w-full md:flex-row md:items-center md:px-0 md:gap-2">
            <Button
              variant="ghost"
              iconComponent={Search}
              onClick={onOpenSearch}
              title="Open Support Search"
              className="hidden md:block"
              data-testid="dashboard-nav-search-open-modal"
            />
            {shouldShowChat && (
              <ChatButtonContainer containerClassName="hidden md:block" />
            )}
            <NotificationsNavButtonContainer
              onClick={(e) => onNavigationClick(e as any, "/notifications")}
              className="hidden md:block"
            />
            <PlatformAvatarDropdown
              photo={user?.photo}
              fullName={fullName}
              isLoaded={!!user}
              onNavigationClick={onNavigationClick}
              menuItems={[
                {
                  title: "Program",
                  href: "/dashboard",
                },
                {
                  title: "Support",
                  href: "/support",
                },
                {
                  title: "Settings",
                  href: "/account",
                },
                {
                  title: "Logout",
                  href: "/logout",
                },
              ]}
            />
          </div>
        </div>
      </div>
    </>
  );
}

export default DashboardNavigationItems;
